import PriorityHighRoundedIcon from "@mui/icons-material/PriorityHighRounded";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Chip from "@mui/material/Chip";
import Stack from "@mui/material/Stack";
import Typography from "@mui/material/Typography";
import { CustomLink } from "core/components/CustomLink";
import { useFormatDate } from "core/hooks/useFormatDate";
import { getImageSRC } from "features/NewsEvents/utils";
import Image from "next/legacy/image";
import { useTranslation } from "react-i18next";

export interface NewsWidgetTileProps {
  id: string;
  title: string;
  blurb: string;
  publishedTime: Date | string;
  bannerBlobName: string;
  bannerAltText: string;
  blurDataUrl: string;
  typeId?: string;
  typeName?: string;
  isImportant?: boolean;
}
export function NewsWidgetTile({
  id,
  title,
  blurb,
  publishedTime,
  bannerBlobName,
  bannerAltText,
  blurDataUrl,
  typeId,
  typeName,
  isImportant,
}: NewsWidgetTileProps) {
  const { t } = useTranslation("NewsEvents", {
    keyPrefix: "Next:NewsEvents:NewsWidgetTile",
  });
  const formattedDate = useFormatDate()(publishedTime);
  const imageSRC = getImageSRC(bannerBlobName);
  return (
    <Stack
      component="article"
      minHeight="250px"
      direction="row"
      flexWrap="wrap"
      justifyContent="space-evenly"
      gap={4}
      sx={{
        paddingBlock: 4,
        "&:not(:last-of-type)": {
          borderBottom: (theme) => `1px solid ${theme.palette.divider}`,
        },
        "& .tileImage": {
          borderRadius: (theme) => theme.shape.borderRadius,
        },
      }}
    >
      <Box
        position="relative"
        sx={{ aspectRatio: "auto 5 / 4" }}
        width="min(275px, 100%)"
        display="flex"
        flexDirection="column"
      >
        <CustomLink href={`/news/${id}`} sx={{ flex: 1, position: "relative" }}>
          <Image
            src={imageSRC}
            alt={bannerAltText}
            layout="fill"
            objectFit="cover"
            className="tileImage"
            objectPosition="50% 50%"
            placeholder="blur"
            blurDataURL={blurDataUrl}
          />
        </CustomLink>
        {isImportant && (
          <PriorityHighRoundedIcon
            sx={{
              zIndex: 1,
              position: "absolute",
              top: ({ spacing }) => spacing(1),
              left: ({ spacing }) => spacing(1),
              color: "error.main",
            }}
          />
        )}
        {typeId && (
          <CustomLink href={`/news?types=${typeId}`} passHref>
            <Chip
              clickable
              size="small"
              color="primary"
              label={typeName}
              sx={{
                zIndex: 1,
                position: "absolute",
                top: ({ spacing }) => spacing(1),
                right: ({ spacing }) => spacing(1),
              }}
            />
          </CustomLink>
        )}
      </Box>

      <Stack
        sx={{
          justifyContent: "space-between",
          alignItems: "start",
          flex: 1,
          display: "flex",
          maxWidth: "66ch",
          minWidth: "min(225px,100%)",
        }}
      >
        <Box>
          <CustomLink href={`/news/${id}`} passHref>
            <Typography variant="subtitle1" fontWeight="bold">
              {title}
            </Typography>
          </CustomLink>
          <Typography variant="body2" color="text.secondary" mb={2}>
            {formattedDate}
          </Typography>
          <Typography
            sx={{
              display: "-webkit-box",
              overflow: "hidden",
              WebkitBoxOrient: "vertical",
              WebkitLineClamp: 5,
            }}
            mb={2}
            variant="body2"
          >
            {blurb}
          </Typography>
        </Box>
        <CustomLink href={`/news/${id}`} passHref sx={{ alignSelf: "end" }}>
          <Button>{t("readMore")}</Button>
        </CustomLink>
      </Stack>
    </Stack>
  );
}
